import { NodeDocContent } from "./nodeDocsData";

export const actionNodesDocs: Record<string, NodeDocContent> = {
  TRANSFORM: {
    title: "Transform Node",
    subtitle: "Field mapping and data transformation",
    color: "indigo",
    badge: "Action",
    description:
      "Maps data from one structure to another using a visual drag-and-drop interface. Rename fields, restructure nested objects, or combine multiple inputs into a clean output schema — no code required.",
    content: `
      <div class="space-y-10">
        <div class="wf-callout wf-callout-info">
          The Transform Node uses a <strong>three-panel layout</strong>: available inputs on the left, field mapping in the center, and a live JSON preview on the right.
        </div>

        <section>
          <h2>How it works</h2>
          <p>The node receives data from connected upstream nodes and lets you visually define how each source field maps to a target field in a new output structure. Drag fields from the left panel into the center mapping area, name your target fields, and the node produces a clean restructured output.</p>
          <div class="wf-steps">
            <div class="wf-step">
              <div class="wf-step-num">1</div>
              <div>
                <p class="wf-step-title">Open the mapping editor</p>
                <p class="wf-step-desc">Click the node to open the three-panel configuration modal.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">2</div>
              <div>
                <p class="wf-step-title">Browse available inputs</p>
                <p class="wf-step-desc">The left sidebar shows all outputs from connected upstream nodes. Expand nested objects to see every available field.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">3</div>
              <div>
                <p class="wf-step-title">Create field mappings</p>
                <p class="wf-step-desc">Drag a source field into the mapping area, then set the target field name. Add as many mappings as needed.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num wf-step-done">&#10003;</div>
              <div>
                <p class="wf-step-title">Preview and save</p>
                <p class="wf-step-desc">The right panel shows a live JSON preview of the output structure. Save when ready.</p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2>Parameters</h2>
          <table>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Description</th>
                <th>Required</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>Source Field</code></td>
                <td>The input field to map from. Drag from the available inputs panel or type a field path manually.</td>
                <td>Yes</td>
              </tr>
              <tr>
                <td><code>Target Field</code></td>
                <td>The output field name in the new structure. This is what downstream nodes will reference.</td>
                <td>Yes</td>
              </tr>
            </tbody>
          </table>
          <p>At least one source-to-target mapping is required. Add multiple mappings to build a complete output schema.</p>
        </section>

        <section>
          <h2>Output</h2>
          <p>A structured JSON object based on your target field mappings. Each target field contains either a template reference (before execution) or the actual mapped value (after execution).</p>
          <table>
            <thead>
              <tr>
                <th>Field</th>
                <th>Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr><td><code>outputs</code></td><td>object</td><td>JSON object with all target fields populated from source data</td></tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Common use cases</h2>
          <div class="wf-use-cases">
            <div class="wf-use-case">
              <p class="wf-use-case-title">API payload preparation</p>
              <p class="wf-use-case-flow">OCR Extraction &rarr; Transform Node &rarr; HTTP Request</p>
              <p>Reshape OCR output fields to match an external API's expected payload format before making the request.</p>
            </div>
            <div class="wf-use-case">
              <p class="wf-use-case-title">Database field mapping</p>
              <p class="wf-use-case-flow">Smart Extraction &rarr; Transform Node &rarr; MongoDB Connector</p>
              <p>Map extracted fields (vendor, amount, date) to your database column names before inserting.</p>
            </div>
            <div class="wf-use-case">
              <p class="wf-use-case-title">Field merging and renaming</p>
              <p class="wf-use-case-flow">Any Node &rarr; Transform Node &rarr; PDF Generator</p>
              <p>Combine multiple fields (first name + last name) or rename them to match template variables before generating a PDF.</p>
            </div>
          </div>
        </section>

        <div class="wf-callout wf-callout-tip">
          <p class="wf-callout-label">Tips</p>
          <ul>
            <li>Use the right panel's JSON preview to verify your mapping output before running the workflow.</li>
            <li>Nested objects and arrays from upstream nodes are fully supported — expand them in the left panel.</li>
            <li>You can use JavaScript expressions in source fields for complex transformations during mapping.</li>
            <li>The node shows a green "Configured" badge when at least one valid mapping exists.</li>
          </ul>
        </div>
      </div>
    `,
  },

  CODE: {
    title: "Code Node",
    subtitle: "Execute custom JavaScript or Python",
    color: "amber",
    badge: "Action",
    description:
      "Write and execute custom JavaScript or Python code within your workflow. Access upstream data through named inputs, apply custom logic, and return results for downstream nodes.",
    content: `
      <div class="space-y-10">
        <div class="wf-callout wf-callout-info">
          Choose between <strong>JavaScript (ES6+)</strong> and <strong>Python</strong>. The code editor includes syntax highlighting and you can define named input parameters that map to upstream node outputs.
        </div>

        <section>
          <h2>How it works</h2>
          <p>The Code Node provides a built-in editor where you write an async function. You define named input parameters that pull data from upstream nodes, then your code processes that data and returns the result.</p>
          <div class="wf-steps">
            <div class="wf-step">
              <div class="wf-step-num">1</div>
              <div>
                <p class="wf-step-title">Select language</p>
                <p class="wf-step-desc">Choose JavaScript or Python from the language dropdown at the top of the editor.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">2</div>
              <div>
                <p class="wf-step-title">Define inputs</p>
                <p class="wf-step-desc">Add named input parameters (e.g. <code>arg1</code>, <code>arg2</code>) and map them to upstream node outputs by dragging variables from the left panel.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">3</div>
              <div>
                <p class="wf-step-title">Write your code</p>
                <p class="wf-step-desc">Access inputs via the <code>inputs</code> object (JS) or <code>inputs</code> dict (Python). Apply your logic and return the result.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num wf-step-done">&#10003;</div>
              <div>
                <p class="wf-step-title">View output</p>
                <p class="wf-step-desc">After execution, the right panel shows the returned data with type badges and syntax highlighting.</p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2>Code example</h2>
          <div class="wf-code-block">
            <div class="wf-code-header">main.js</div>
            <pre><code>async function run(inputs) {
  // inputs.arg1 contains data from an upstream node
  const { data } = inputs.arg1;

  // Custom logic — filter, transform, calculate
  const processed = data.map(item => ({
    ...item,
    total: item.quantity * item.price,
    processedAt: new Date().toISOString()
  }));

  return { items: processed, count: processed.length };
}</code></pre>
          </div>
        </section>

        <section>
          <h2>Parameters</h2>
          <table>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Description</th>
                <th>Required</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>Language</code></td>
                <td>JavaScript (ES6+) or Python. Determines the runtime and syntax highlighting.</td>
                <td>Yes</td>
              </tr>
              <tr>
                <td><code>Inputs</code></td>
                <td>Named key-value pairs. Keys become properties on the <code>inputs</code> object. Values are mapped from upstream node outputs via drag-and-drop.</td>
                <td>No</td>
              </tr>
              <tr>
                <td><code>Code</code></td>
                <td>The function body. Must be an async function that receives <code>inputs</code> and returns a value.</td>
                <td>Yes</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Runtime</h2>
          <table>
            <thead>
              <tr>
                <th>Spec</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>JavaScript engine</td><td>Node.js v18.x (LTS)</td></tr>
              <tr><td>Execution timeout</td><td>30 seconds</td></tr>
              <tr><td>Memory limit</td><td>128 MB</td></tr>
              <tr><td>Network access</td><td>Disabled — use the HTTP Request node instead</td></tr>
              <tr><td>File system</td><td>Restricted — sandboxed environment</td></tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Output</h2>
          <p>The return value of your function becomes the node's output. It can be a primitive value or a structured object with multiple fields. Output fields are displayed with type badges in the right panel.</p>
          <table>
            <thead>
              <tr>
                <th>Field</th>
                <th>Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr><td><code>outputs</code></td><td>any</td><td>Whatever your function returns — object, array, string, or number</td></tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Common use cases</h2>
          <div class="wf-use-cases">
            <div class="wf-use-case">
              <p class="wf-use-case-title">Complex transformations</p>
              <p class="wf-use-case-flow">OCR Extraction &rarr; Code Node &rarr; MongoDB</p>
              <p>Apply regex parsing, date formatting, math operations, or custom business logic that the Transform Node can't handle.</p>
            </div>
            <div class="wf-use-case">
              <p class="wf-use-case-title">Data validation</p>
              <p class="wf-use-case-flow">Smart Extraction &rarr; Code Node &rarr; If-Else</p>
              <p>Run custom validation rules (required fields, format checks, range validation) and return a pass/fail flag for conditional routing.</p>
            </div>
            <div class="wf-use-case">
              <p class="wf-use-case-title">Array manipulation</p>
              <p class="wf-use-case-flow">HTTP Request &rarr; Code Node &rarr; For Each</p>
              <p>Filter, sort, group, or deduplicate arrays of items before feeding them into a loop.</p>
            </div>
          </div>
        </section>

        <div class="wf-callout wf-callout-tip">
          <p class="wf-callout-label">Tips</p>
          <ul>
            <li>Click "Load Example" to pre-fill the editor with a starter template for the selected language.</li>
            <li>Scripts run in a secure, isolated sandbox — no file system access or environment variables.</li>
            <li>For API calls, use the HTTP Request node instead. Network access is disabled in the Code Node for security.</li>
            <li>The right panel shows execution output with type badges (string, number, object, array) for each returned field.</li>
          </ul>
        </div>
      </div>
    `,
  },

  "HTTP-API": {
    title: "HTTP Request",
    subtitle: "Call external APIs mid-workflow",
    color: "violet",
    badge: "Action",
    description:
      "Send HTTP requests to any external API during workflow execution. Configure the method, URL, authentication, headers, query parameters, and request body — with drag-and-drop variable support throughout.",
    content: `
      <div class="space-y-10">
        <div class="wf-callout wf-callout-info">
          This is an <strong>action node</strong> that calls APIs mid-workflow. It's different from the HTTP Trigger, which exposes your workflow as an API endpoint. Use this node to <em>send</em> requests, not receive them.
        </div>

        <section>
          <h2>How it works</h2>
          <p>Configure a target URL, HTTP method, optional authentication, and payload. When the workflow reaches this node, it sends the request, waits for the response, and passes the response data to downstream nodes.</p>
          <div class="wf-steps">
            <div class="wf-step">
              <div class="wf-step-num">1</div>
              <div>
                <p class="wf-step-title">Set method and URL</p>
                <p class="wf-step-desc">Choose the HTTP method (GET, POST, PUT, DELETE, PATCH) and enter the endpoint URL. Drag variables from the left panel into the URL for dynamic endpoints.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">2</div>
              <div>
                <p class="wf-step-title">Configure authentication</p>
                <p class="wf-step-desc">Select "No Auth" or "Bearer Token" and provide the token value. Token field supports drag-and-drop variables.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">3</div>
              <div>
                <p class="wf-step-title">Add query params, headers, and body</p>
                <p class="wf-step-desc">Toggle on optional sections to add key-value pairs. All value fields support dragging variables from upstream nodes.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num wf-step-done">&#10003;</div>
              <div>
                <p class="wf-step-title">Execute and receive response</p>
                <p class="wf-step-desc">The API response JSON is displayed in the right panel and passed to connected downstream nodes.</p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2>Supported methods</h2>
          <div class="wf-method-list">
            <code>GET</code>
            <code>POST</code>
            <code>PUT</code>
            <code>DELETE</code>
            <code>PATCH</code>
          </div>
        </section>

        <section>
          <h2>Parameters</h2>
          <table>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Description</th>
                <th>Required</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>Method</code></td>
                <td>HTTP method — each has a color-coded indicator (GET = green, POST = amber, PUT = blue, DELETE = red, PATCH = violet).</td>
                <td>Yes</td>
              </tr>
              <tr>
                <td><code>URL</code></td>
                <td>Full endpoint URL. Supports <code>{{variable}}</code> syntax and drag-and-drop from the variable panel.</td>
                <td>Yes</td>
              </tr>
              <tr>
                <td><code>Authentication</code></td>
                <td>Select "No Auth" or "Bearer Token". When Bearer is selected, provide the token value.</td>
                <td>No</td>
              </tr>
              <tr>
                <td><code>Query Parameters</code></td>
                <td>Key-value pairs appended to the URL as query strings. Toggle the section on to add them.</td>
                <td>No</td>
              </tr>
              <tr>
                <td><code>Headers</code></td>
                <td>Custom HTTP headers (e.g. <code>Content-Type</code>, <code>Authorization</code>). Toggle to add.</td>
                <td>No</td>
              </tr>
              <tr>
                <td><code>Body</code></td>
                <td>Request body key-value pairs for POST, PUT, or PATCH requests. Toggle to add.</td>
                <td>No</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Dynamic URLs</h2>
          <p>Drag variables from the left panel directly into the URL field, or use <code>{{variable}}</code> syntax to build dynamic endpoints:</p>
          <div class="wf-code-block">
            <div class="wf-code-header">Example</div>
            <pre><code>https://api.example.com/users/{{user_id}}/documents/{{doc_id}}</code></pre>
          </div>
          <p>Variables are resolved at execution time from upstream node outputs.</p>
        </section>

        <section>
          <h2>Output</h2>
          <p>The full API response is parsed and made available to downstream nodes.</p>
          <table>
            <thead>
              <tr>
                <th>Field</th>
                <th>Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr><td><code>outputs</code></td><td>object</td><td>The parsed JSON response body from the API call</td></tr>
            </tbody>
          </table>
          <p>The right panel shows the response with syntax highlighting and line numbers after execution.</p>
        </section>

        <section>
          <h2>Common use cases</h2>
          <div class="wf-use-cases">
            <div class="wf-use-case">
              <p class="wf-use-case-title">Data enrichment</p>
              <p class="wf-use-case-flow">OCR Extraction &rarr; HTTP Request &rarr; Transform &rarr; MongoDB</p>
              <p>After extracting a product code via OCR, call an external pricing API to fetch current prices, then store the enriched data.</p>
            </div>
            <div class="wf-use-case">
              <p class="wf-use-case-title">Third-party notification</p>
              <p class="wf-use-case-flow">If-Else &rarr; HTTP Request &rarr; Transform</p>
              <p>Call a Slack, Teams, or SMS API to send notifications based on conditional routing results.</p>
            </div>
            <div class="wf-use-case">
              <p class="wf-use-case-title">CRM integration</p>
              <p class="wf-use-case-flow">Smart Extraction &rarr; Code Node &rarr; HTTP Request</p>
              <p>Push extracted document data to Salesforce, HubSpot, or any CRM via their REST API.</p>
            </div>
          </div>
        </section>

        <div class="wf-callout wf-callout-tip">
          <p class="wf-callout-label">Tips</p>
          <ul>
            <li>This node <strong>sends</strong> requests. To <strong>receive</strong> requests and trigger a workflow, use the HTTP Trigger node instead.</li>
            <li>All value fields (URL, token, query params, headers, body) support drag-and-drop from the variable panel.</li>
            <li>Toggle sections (Query Params, Headers, Body) on/off using the switch controls — only enabled sections are included in the request.</li>
            <li>Chain with a Transform node after this node to extract specific fields from the API response.</li>
          </ul>
        </div>
      </div>
    `,
  },

  PDF: {
    title: "PDF Generator",
    subtitle: "Create PDF documents from templates",
    color: "red",
    badge: "Action",
    description:
      "Generate professional PDF documents from rich HTML templates with dynamic data injection. Design your layout in a WYSIWYG editor, embed variables from upstream nodes, and produce downloadable PDFs.",
    content: `
      <div class="space-y-10">
        <div class="wf-callout wf-callout-info">
          The editor supports <strong>rich text (WYSIWYG)</strong> and <strong>raw HTML</strong> with full CSS styling. Drag variables from the left panel directly into your template to inject dynamic data.
        </div>

        <section>
          <h2>How it works</h2>
          <p>Design a document template using the built-in rich text editor, inject variables from upstream nodes as placeholders, and the node renders a formatted PDF on execution.</p>
          <div class="wf-steps">
            <div class="wf-step">
              <div class="wf-step-num">1</div>
              <div>
                <p class="wf-step-title">Set input data source</p>
                <p class="wf-step-desc">Drag the complete outputs object from an upstream node into the input data source bar. This provides the data context for variable injection.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">2</div>
              <div>
                <p class="wf-step-title">Design your template</p>
                <p class="wf-step-desc">Use the WYSIWYG rich text editor to build your layout. Add headings, tables, lists, and any formatted content.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">3</div>
              <div>
                <p class="wf-step-title">Inject variables</p>
                <p class="wf-step-desc">Drag fields from the left panel into the template. Variables are embedded using the <code>NodeName.outputs.fieldName</code> format and are replaced with real data at execution.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num wf-step-done">&#10003;</div>
              <div>
                <p class="wf-step-title">Generate PDF</p>
                <p class="wf-step-desc">On execution, the template is rendered with actual values and converted to a PDF file.</p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2>Parameters</h2>
          <table>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Description</th>
                <th>Required</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>Input Data Source</code></td>
                <td>The upstream node's complete outputs object. Drag the entire outputs block (not individual fields) into the drop zone.</td>
                <td>Conditional</td>
              </tr>
              <tr>
                <td><code>Template</code></td>
                <td>The HTML/rich text template for the PDF layout. Supports full CSS styling and variable placeholders.</td>
                <td>Conditional</td>
              </tr>
            </tbody>
          </table>
          <p>Either a template or an input data source is required. If no template is configured, the input data source must be provided.</p>
        </section>

        <section>
          <h2>Output</h2>
          <table>
            <thead>
              <tr>
                <th>Field</th>
                <th>Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr><td><code>pdf_file</code></td><td>file</td><td>The generated PDF file object — can be sent to email, storage, or webhook nodes</td></tr>
              <tr><td><code>file_name</code></td><td>string</td><td>The generated filename</td></tr>
              <tr><td><code>file_size</code></td><td>number</td><td>File size in bytes</td></tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Common use cases</h2>
          <div class="wf-use-cases">
            <div class="wf-use-case">
              <p class="wf-use-case-title">Invoice generation</p>
              <p class="wf-use-case-flow">Smart Extraction &rarr; Transform &rarr; PDF Generator &rarr; OneDrive Upload</p>
              <p>Extract invoice data via AI, map fields to template variables, generate a formatted invoice PDF, and save to cloud storage.</p>
            </div>
            <div class="wf-use-case">
              <p class="wf-use-case-title">Summary reports</p>
              <p class="wf-use-case-flow">LLM Node &rarr; PDF Generator &rarr; Webhook Export</p>
              <p>Generate an AI summary of a document, produce a branded PDF report, and send it to an external system via webhook.</p>
            </div>
            <div class="wf-use-case">
              <p class="wf-use-case-title">Certificate generation</p>
              <p class="wf-use-case-flow">HTTP Trigger &rarr; Code Node &rarr; PDF Generator &rarr; MongoDB</p>
              <p>Receive participant data via API, format it with custom logic, generate a certificate PDF, and store the record.</p>
            </div>
          </div>
        </section>

        <div class="wf-callout wf-callout-tip">
          <p class="wf-callout-label">Tips</p>
          <ul>
            <li>Use full CSS in your template for professional layouts — fonts, colors, borders, spacing are all supported.</li>
            <li>Drag the <strong>entire outputs object</strong> (not individual fields) into the input data source bar.</li>
            <li>The output PDF can be piped directly to OneDrive Upload, Webhook Export, or Excel Export nodes.</li>
            <li>Test your template by running the workflow and checking the right panel for execution results.</li>
          </ul>
        </div>
      </div>
    `,
  },
};
